export const ROLES = [
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'unassigned', label: 'Unassigned' },
];

export const APPS = [
  { value: 'kiosk', label: 'Kiosk', packagename: 'com.thisway.kioskv2' },
  { value: 'kitchen', label: 'Kitchen', packagename: 'com.thisway.kitchen_v2' },
  { value: 'agent', label: 'Agent', packagename: 'co.thisway.agent' },
];

export const PAGE_SIZE = 25;
export const POLL_MS = 30000;
export const ROLLOUT_POLL_MS = 10000;
export const BULK_CONFIRM_THRESHOLD = 10;
export const BULK_MAX = 300;
export const ROLLOUT_DEFAULTS = { batchsize: 5, batchintervalminutes: 10, maxfailures: 2 };
export const ENROLL_SCRIPT = './enroll.sh';

const isBlank = (v) => v === undefined || v === null || v === '';

export function buildDeviceListQuery({
  search,
  role,
  status,
  companyid,
  storeid,
  outdated,
  tag,
  page,
  pageSize = PAGE_SIZE,
  ordercolumn,
  orderby,
} = {}) {
  const parts = [`pageNumber=${page}`, `pageSize=${pageSize}`];
  const add = (name, value) => {
    if (!isBlank(value)) parts.push(`${name}=${encodeURIComponent(value)}`);
  };
  add('search', search);
  if (role !== 'all') add('role', role);
  add('status', status);
  add('companyid', companyid);
  add('storeid', storeid);
  if (outdated) parts.push('outdated=true');
  add('tag', tag);
  add('ordercolumn', ordercolumn);
  add('orderby', orderby);
  return `?${parts.join('&')}`;
}

const PACKAGE_OPTIONS = APPS.map((a) => a.packagename);
const packageArg = { name: 'package', kind: 'package', options: PACKAGE_OPTIONS };

export const COMMAND_TYPES = [
  { type: 'ping', label: 'Ping', description: 'Checks that the device answers', args: [], risky: false },
  {
    type: 'screenshot',
    label: 'Screenshot',
    description: 'Captures what the screen shows right now',
    args: [],
    risky: false,
  },
  {
    type: 'reboot',
    label: 'Reboot',
    description: 'Restarts the tablet; it is offline for a minute or two',
    args: [],
    risky: true,
  },
  {
    type: 'restart_app',
    label: 'Restart app',
    description: 'Force-stops a Thisway app and starts it again',
    args: [packageArg],
    risky: false,
  },
  {
    type: 'clear_app_cache',
    label: 'Clear app cache',
    description: 'Clears the cached files of a Thisway app',
    args: [packageArg],
    risky: false,
  },
  {
    type: 'collect_logs',
    label: 'Collect logs',
    description: 'Uploads the latest log lines from the device',
    args: [{ name: 'lines', kind: 'int', min: 1, max: 5000 }],
    risky: false,
  },
  {
    type: 'set_brightness',
    label: 'Set brightness',
    description: 'Sets the screen brightness in percent',
    args: [{ name: 'percent', kind: 'int', min: 10, max: 100 }],
    risky: false,
  },
  {
    type: 'set_volume',
    label: 'Set volume',
    description: 'Sets the media volume in percent',
    args: [{ name: 'percent', kind: 'int', min: 0, max: 100 }],
    risky: false,
  },
  {
    type: 'wifi_reconnect',
    label: 'Reconnect Wi-Fi',
    description: 'Drops and rejoins the Wi-Fi network; the device is briefly unreachable',
    args: [],
    risky: true,
  },
  {
    type: 'set_locked_app',
    label: 'Set locked app',
    description: 'Chooses which Thisway app the device is locked to',
    args: [packageArg],
    risky: false,
  },
  {
    type: 'agent_config',
    label: 'Agent settings',
    description: 'Changes how often the agent reports in',
    args: [
      { name: 'heartbeatseconds', kind: 'int', min: 30, max: 600 },
      { name: 'telemetryseconds', kind: 'int', min: 60, max: 3600 },
    ],
    risky: false,
  },
  {
    type: 'maintenance',
    label: 'Maintenance mode',
    description: 'Lifts the kiosk lock, status bar and USB debugging for 30 minutes',
    args: [],
    risky: true,
  },
  {
    type: 'end_maintenance',
    label: 'End maintenance',
    description: 'Closes the maintenance window now: the kiosk lock, status bar and USB debugging come back',
    args: [],
    risky: false,
  },
];

export function validateCommandArgs(type, args) {
  const command = COMMAND_TYPES.find((c) => c.type === type);
  if (!command) return { ok: false, error: 'Unknown command' };
  const input = args || {};
  const out = {};
  for (const arg of command.args) {
    const raw = input[arg.name];
    if (arg.kind === 'package') {
      if (!arg.options.includes(raw)) {
        return { ok: false, error: `${arg.name} must be one of the fleet packages` };
      }
      out[arg.name] = raw;
    } else {
      const n = typeof raw === 'string' ? (raw.trim() === '' ? NaN : Number(raw)) : raw;
      if (typeof n !== 'number' || !Number.isInteger(n) || n < arg.min || n > arg.max) {
        return { ok: false, error: `${arg.name} must be between ${arg.min} and ${arg.max}` };
      }
      out[arg.name] = n;
    }
  }
  return { ok: true, args: out };
}

export const needsTypedConfirm = (count) => count > BULK_CONFIRM_THRESHOLD;

const TONES = {
  online: 'emerald',
  done: 'emerald',
  running: 'emerald',
  offline: 'slate',
  cancelled: 'slate',
  expired: 'slate',
  queued: 'slate',
  sent: 'blue',
  acked: 'blue',
  paused: 'amber',
  failed: 'rose',
};

export const statusTone = (status) => TONES[status] || 'slate';

export function statusLabel(status) {
  if (!status) return '';
  if (status === 'unassigned') return 'Awaiting assignment';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function relativeSeen(iso, now = Date.now()) {
  if (!iso) return '—';
  const seconds = Math.max(0, (now - Date.parse(iso)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function rolloutSummary(deviceCount, batchsize, intervalMin) {
  const batches = Math.ceil(deviceCount / batchsize);
  const minutes = Math.max(0, batches - 1) * intervalMin;
  return { batches, minutes, text: `${deviceCount} devices, ${batches} batches, ≈ ${minutes} min` };
}

export const enrollCommand = ({ token, apiUrl }) =>
  `${ENROLL_SCRIPT} <adb-address> ${token} ${apiUrl}`;

export const isTerminalRollout = (status) => ['done', 'cancelled', 'failed'].includes(status);

// Only http(s) URLs become a link or an image source; anything else (a
// javascript: or data: value) stays text (security carry D1).
export const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

export const isTerminalCommand = (status) =>
  ['done', 'failed', 'expired', 'cancelled'].includes(status);

export const holdMinutes = (intervalMin) => Math.max(60, 3 * intervalMin);

export const offlineHoldText = (intervalMin) =>
  `An offline device holds the next batch for up to ${holdMinutes(intervalMin)} min, then the rollout continues without it; its install stays pending.`;

export const rolloutRoleFor = (app) => (app === 'kiosk' || app === 'kitchen' ? app : 'all');
